import {Box, HStack, Text, VStack} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

import {useState} from 'react';

import {clamp, isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getBorderGradient, PERCENTAGE_MULTIPLIER, SEGMENT_COLORS} from '../utils/getBorderGradient';

const CIRCLE_BORDER_WIDTH = 4;

interface AttractivenessBarProps extends TestIdProps {
  value: number | Nullish;
  title: string;
  lowLabel: string;
  highLabel: string;
}

export function AttractivenessBar(props: AttractivenessBarProps) {
  const theme = useTheme();
  const [barWidth, setBarWidth] = useState(0);
  const [circleDiameter, setCircleDiameter] = useState(0);

  const barRef = (node: HTMLDivElement | null) => {
    if (node) {
      setBarWidth(node.offsetWidth);
    }
  };

  const circleRef = (node: HTMLDivElement | null) => {
    if (node) {
      setCircleDiameter(node.offsetWidth);
    }
  };

  if (isNil(props.value)) {
    return null;
  }

  const rawPercentage = Math.round(props.value * PERCENTAGE_MULTIPLIER);
  const percentage = Number.isFinite(rawPercentage)
    ? clamp(0, PERCENTAGE_MULTIPLIER, rawPercentage)
    : 0;
  const borderGradient = getBorderGradient(barWidth, circleDiameter, percentage);

  return (
    <VStack data-testid={suffixTestId('attractivenessGauge', props)}>
      <VStack spacing={1}>
        <Text size="xSmall" color="secondary">
          {props.title}
        </Text>

        <Box paddingVertical={4}>
          <Box position="relative" width="100%">
            <div
              ref={barRef}
              css={css`
                display: flex;
                gap: 1px;
              `}
            >
              {SEGMENT_COLORS.map((color, i) => (
                <div
                  key={color}
                  data-testid={suffixTestId('attractivenessGauge-bar', props)}
                  css={css`
                    flex: 1;
                    height: ${theme.getSize(2)};
                    border-radius: ${i === 0 ? theme.getSize(2) : 0}
                      ${i === SEGMENT_COLORS.length - 1 ? theme.getSize(2) : 0}
                      ${i === SEGMENT_COLORS.length - 1 ? theme.getSize(2) : 0}
                      ${i === 0 ? theme.getSize(2) : 0};
                    background: ${color};
                  `}
                />
              ))}
            </div>

            <div
              ref={circleRef}
              data-testid={suffixTestId('attractivenessGauge-indicator', props)}
              css={css`
                position: absolute;
                top: 50%;
                left: ${percentage}%;
                transform: translate(-50%, -50%);
                width: ${theme.getSize(9)};
                height: ${theme.getSize(9)};
                border-radius: 50%;
                border: ${CIRCLE_BORDER_WIDTH}px solid transparent;
                background:
                  linear-gradient(${theme.colors.general.white}, ${theme.colors.general.white})
                    padding-box,
                  ${borderGradient} border-box;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: ${theme.getSize(4)};
              `}
            >
              <Text size="xSmall" color="primary" alternative>
                {percentage}%
              </Text>
            </div>
          </Box>
        </Box>
      </VStack>

      <HStack justify="space-between">
        <Text size="xSmall" color="tertiary">
          {props.lowLabel}
        </Text>

        <Text size="xSmall" color="tertiary">
          {props.highLabel}
        </Text>
      </HStack>
    </VStack>
  );
}
